import React, { useState, useEffect, useRef } from 'react';
import { Calendar } from 'primereact/calendar';
import { Button } from 'primereact/button';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { FilterMatchMode, FilterOperator } from "primereact/api";
import { useSelector, useDispatch } from 'react-redux';
import Moment from 'moment';

import { parseDateTime, addDateTime } from '../../../common/date-time';
import { getNacAutomationLogsInit } from './redux/actions';
import DetailSummaryDialog from './DetailSummaryDialog';
import NacAutomationPayload from './NacAutomationPayload';
import './NacAutomation.scss'

const NacAutomation = () => {

    const nacAutomationLogs = useSelector((state) => state.nacAutomation.nacAutomationLogs);
    const totalRecords = useSelector((state) => state.nacAutomation.totalRecords);

    const tableRef = useRef(null);
    const dispatch = useDispatch();
    const [dateRange, setDateRange] = useState([
        addDateTime("day", -14),
        addDateTime("day", 0)
    ]);
    const [tableFilters, setTableFilters] = useState(null);
    const [dialogData, setDialogData] = useState(null);
    const [alertPayload, setAlertPayload] = useState(null);
    const [filterCondition, setFilterCondition] = useState("");
    const [lazyParams, setLazyParams] = useState({
        first: 0, // Starting index
        rows: 10, // Default page size
        page: 0, // Current page number
        sortField: "receivedDate", // Default sort field
        sortOrder: -1, // Default sort order (-1 for descending, 1 for ascending)
    });

    useEffect(() => {
        initializeTableFilters();
        fetchLogs();
    },[]);

    const initializeTableFilters = () => {
        setTableFilters({
            global: { value: null, matchMode: FilterMatchMode.CONTAINS },
            requestId: {
                operator: FilterOperator.AND,
                constraints: [{ value: null, matchMode: FilterMatchMode.CONTAINS }],
            },
            processType: {
                operator: FilterOperator.AND,
                constraints: [{ value: null, matchMode: FilterMatchMode.CONTAINS }],
            },
            status: {
                operator: FilterOperator.AND,
                constraints: [{ value: null, matchMode: FilterMatchMode.CONTAINS }],
            }
        })
    }

    const fetchLogs = (params=lazyParams,currentFilters=filterCondition) => {
        const sortDirection= params.sortOrder== -1?'DESC' : 'ASC';
        const startDate = Moment(dateRange[0]).format("yyyy-MM-DD");
        const endDate = !!dateRange[1]
            ? Moment(dateRange[1]).format("yyyy-MM-DD")
            : Moment(dateRange[0]).format("yyyy-MM-DD");
        const offset = Moment(new Date()).utcOffset();
        const filterConditionPayload = constructFilterParams(currentFilters);
        const requestParams={
            startDate,
            endDate,
            offset,
            pageNumber: params.page ?? 0, // Current page number
            pageSize: params.rows ?? 10, // Number of rows per page
            sortBy: params.sortField ?? "receivedDate", // Sorting field
            sortDirection: sortDirection ?? "DESC", // Sorting order
            filterCondition: filterConditionPayload ?? "" // **Filter condition**
        };
        dispatch(getNacAutomationLogsInit(requestParams));
    };

    const constructFilterParams = (filterConditions) => {
        if (!filterConditions || typeof filterConditions !== "object") {
        return ""; // Return empty string if no filters are present
        }
        const queryParams = [];
        Object.keys(filterConditions).forEach((key) => {
        const filter = filterConditions[key];
        const constraints = filter?.constraints;
        if (constraints && constraints.length > 0) {
            const { value, matchMode } = constraints[0];
            if (value !== undefined && value !== null && value !== "") {
            queryParams.push(
                `${encodeURIComponent(key)}&${encodeURIComponent(matchMode)}&${encodeURIComponent(value)}`
            );
            }
        }
        });
        return queryParams.join(",");
    };

    const exportLogs = () => tableRef.current.exportCSV();

     /**
     * Handle filtering event
     */
    const onFilter = (filters) => {
        setFilterCondition(filters);
        const updatedParams={
            ...lazyParams, 
            first: 0,
            page: 0
        };
        setLazyParams(updatedParams); // Reset to first page
        fetchLogs(updatedParams,filters); // Fetch filtered data
    };

    /**
     * Handle pagination event
     */
    const onPage = (event) => {
        const updatedParams=
        {
            ...lazyParams,
            first: event.first, // Start index
            rows: event.rows, // Rows per page
            page: event.page // Current page number
        };
        setLazyParams(updatedParams);
        fetchLogs(updatedParams); // Fetch data for the new page
    };

    /**
     * Handle sorting event
     */
    const onSort = (event) => {
    const updatedParams={
        ...lazyParams,
        sortField: event.sortField, // Sorting field
        sortOrder: event.sortOrder // Sorting order
    };
        setLazyParams(updatedParams);
        fetchLogs(updatedParams); // Fetch sorted data
    };

    const receivedDateTemplate = ({receivedDate}) => (
        !!receivedDate ? parseDateTime(receivedDate) : <span>-</span>
    );

    const modifiedDateTemplate = ({modifiedDate}) => (
        !!modifiedDate ? parseDateTime(modifiedDate) : <span>-</span>
    )

    const detailsBodyTemplate = ({requestId, alertPayload}) => (
        <div className="d-flex justify-content-between gap-3">
            <i className="pi pi-info-circle" onClick={() => setDialogData(requestId)}/>
            <i className="pi pi-book" onClick={() => setAlertPayload(alertPayload)}/>
        </div>
    )

    return (
        <div className="nac-automation-container container-fluid">
            <NacAutomationPayload dialogData={alertPayload} setDialogData={setAlertPayload}/>
            <DetailSummaryDialog dialogData={dialogData} setDialogData={setDialogData}/>
                <p className="page-label">Automation Logs
                <i className="pi pi-angle-right" />
                Network Automation
                <i className="pi pi-angle-right" />
                NAC
                </p> 
            <div className="row nac-automation-header">
                <div className="col-12 col-6 input-field">
                <label htmlFor="dateRange">Events Date</label>
                <Calendar
                id="dateRange"
                value={dateRange}
                onChange={(e) => setDateRange(e.value)}
                minDate={addDateTime("month", -3)}
                maxDate={addDateTime("month", 0)}
                selectionMode="range"
                showIcon
                />
                </div>
                <div className="col-12 buttons">
                    <Button label="Get Logs" onClick={fetchLogs}/>
                    <span className="button-container">
                        <Button icon="pi pi-refresh" className="p-button-text" onClick={fetchLogs}/>
                        <Button icon="pi pi-download" className="p-button-text" onClick={exportLogs}/>
                    </span>
                </div>
            </div>
            <div className="nac-automation-content">
                <DataTable
                value={nacAutomationLogs}
                ref={tableRef}
                filters={tableFilters}
                filterDisplay="menu"
                sortField={lazyParams.sortField}
                sortOrder={ lazyParams.sortOrder}
                lazy
                first={lazyParams.first}
                rows={lazyParams.rows}
                onPage={onPage}
                onSort={onSort}
                onFilter={(e) => onFilter(e.filters)}
                paginator
                totalRecords={totalRecords} // Replace with actual total records count
                paginatorTemplate="CurrentPageReport RowsPerPageDropdown FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink"
                currentPageReportTemplate="Items per page"
                rowsPerPageOptions={[10,20,50]}
                emptyMessage="No logs found."
                >
                    <Column field="requestId" header="RITM" sortable filter/>
                    <Column field="processType" header="Process Type" sortable filter/>
                    <Column field="receivedDate" header="Received Date" body={receivedDateTemplate} sortable/>
                    <Column field="modifiedDate" header="Modified Date" body={modifiedDateTemplate} sortable/>
                    <Column field="status" header="Status" sortable filter/>
                    <Column header="Details" body={detailsBodyTemplate}/>
                </DataTable>

            </div>
        </div>
    )
}

export default NacAutomation;